import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { riskApi } from '../../api/riskApi';
import { RiskScoreBadge } from '../../components/Risk/RiskScoreBadge';
import { ObstacleDataTable } from '../../components/Risk/ObstacleDataTable';
import { Loader2, ArrowLeft, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';

export const RiskReportDetail = () => {
    const { jobId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();

    const [search, setSearch] = useState(null);
    const [report, setReport] = useState(null);
    const [status, setStatus] = useState('PENDING'); // PENDING | RUNNING | FAILED | FAILED_TEMPORARY | COMPLETED
    const [error, setError] = useState(null);

    const isPolling = useRef(false);
    const intervalRef = useRef(null);

    const fetchStatus = async () => {
        if (isPolling.current) return;
        isPolling.current = true;

        try {
            const res = await riskApi.getJobStatus(token, jobId);

            if (res.search) {
                setSearch(res.search);
                setStatus(res.search.status);

                // Stop polling if reached terminal states
                if (['COMPLETED', 'FAILED', 'FAILED_TEMPORARY'].includes(res.search.status)) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    if (res.report) {
                        setReport(res.report);
                    }
                }
            }
        } catch (err) {
            console.error("Polling error", err);
            // Optionally clear interval on hard network fetch failures?
            // We'll let it retry a few times.
            setError("Error comunicándose con el servidor...");
        } finally {
            isPolling.current = false;
        }
    };

    useEffect(() => {
        // Initial Fetch
        fetchStatus();

        // Setup Interval
        intervalRef.current = setInterval(fetchStatus, 3000);

        // Cleanup interval on unmount
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, token]);

    const handleRetry = () => {
        navigate('/risk/new');
    };

    if (!search && !error) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto py-8">
            <button
                onClick={() => navigate('/risk')}
                className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-6 transition"
            >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Volver al Historial
            </button>

            {/* Header Block Always Show if we have search */}
            {search && (
                <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-8 flex flex-col md:flex-row justify-between md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{search.proposed_name}</h1>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {search.business_description}
                        </p>
                    </div>
                    <div className="text-right flex flex-col items-start md:items-end">
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Clases Analizadas</span>
                        <div className="flex gap-1 flex-wrap justify-end">
                            {(search.confirmed_classes || []).map(c => (
                                <span key={c} className="bg-gray-100 border border-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-semibold">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Polling State */}
            {['PENDING', 'RUNNING'].includes(status) && (
                <div className="bg-white rounded-xl shadow border border-gray-200 p-12 flex flex-col items-center justify-center text-center">
                    <Loader2 className="h-12 w-12 text-indigo-500 animate-spin mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Analizando el acervo marcario del IMPI...</h2>
                    <p className="text-gray-500 max-w-md">Consultando acervos... Analizando viabilidad frente a registros vigentes y en trámite. Este proceso puede tomar unos segundos.</p>
                </div>
            )}

            {/* Failure State (Transient) */}
            {status === 'FAILED_TEMPORARY' && (
                <div className="bg-yellow-50 rounded-xl shadow border border-yellow-200 p-8 flex flex-col items-center text-center">
                    <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
                    <h2 className="text-lg font-semibold text-yellow-800 mb-2">Error Temporal de Conexión</h2>
                    <p className="text-yellow-700 max-w-lg mb-6 text-sm">El servidor del IMPI superó el tiempo máximo de espera o se encuentra inestable.</p>
                    <Button onClick={handleRetry} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                        Reintentar Análisis
                    </Button>
                </div>
            )}

            {/* Failure State (Permanent) */}
            {status === 'FAILED' && (
                <div className="bg-red-50 rounded-xl shadow border border-red-200 p-8 flex flex-col items-center text-center">
                    <ShieldAlert className="h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-lg font-semibold text-red-800 mb-2">Error Crítico del Sistema</h2>
                    <p className="text-red-700 max-w-lg text-sm mb-4">No fue posible completar el escaneo automatizado debido a un error crítico o bloqueo de acceso (Status: Fallido). Por favor contacte soporte.</p>
                    {search?.error_message && (
                        <div className="bg-white/50 text-red-900 border border-red-200 p-2 rounded text-xs w-full max-w-xl truncate">
                            Debug: {search.error_message}
                        </div>
                    )}
                </div>
            )}

            {/* Success State */}
            {status === 'COMPLETED' && report && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Left Column (Scores) */}
                    <div className="lg:col-span-1 flex flex-col gap-6">
                        <RiskScoreBadge riskLevel={report.overall_risk} />

                        {/* Placeholder AI Card */}
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 opacity-60 grayscale cursor-not-allowed">
                            <h3 className="font-semibold text-gray-800 mb-2 text-sm">Reporte en Lenguaje Natural</h3>
                            <p className="text-xs text-gray-500 mt-2">
                                (Próximamente... Fase 2 AI Generativa)
                            </p>
                            <div className="mt-4 space-y-2">
                                <div className="h-2 bg-gray-200 rounded w-full"></div>
                                <div className="h-2 bg-gray-200 rounded w-5/6"></div>
                                <div className="h-2 bg-gray-200 rounded w-4/6"></div>
                            </div>
                        </div>

                        {/* Analysis Params Meta */}
                        <div className="border border-gray-200 rounded-2xl p-4 text-xs text-gray-500 bg-white">
                            <div className="mb-2"><strong>ID Búsqueda:</strong> <br /><span className="truncate block font-mono bg-gray-100 p-1 mt-1 rounded text-[10px]">{jobId}</span></div>
                            <div className="mb-2"><strong>Fecha:</strong> {new Date(search.created_at).toLocaleString()}</div>
                            <div>
                                <strong>Variantes Exploradas:</strong>
                                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                                    {(search.confirmed_variants || []).map(v => <li key={v}>{v}</li>)}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Right Column (Data Table) */}
                    <div className="lg:col-span-3">
                        <ObstacleDataTable obstacles={report.obstacles} />
                    </div>
                </div>
            )}
        </div>
    );
};
